import { dateToMonthYear, isMonthYearBefore, MonthYearPeriod } from '@common/neo.tax';
import { captureException } from '@sentry/react';
import { BadgeColorVariant } from '@ui-components/neo.tax';

import { CapitalizationStatus } from '@neo.tax/shared';

import { getCapitalizationDateString } from '../organisms/asc-projects-layout/asc-projects-layout.utils';

export type ProjectBadgeConfig = {
  label: string;
  colorVariant: BadgeColorVariant;
  description?: string;
  sortOrdinal: number;
};

// Gray, dateless badges for projects with nothing capitalized in the selected period.
const EXPENSED_BADGE: ProjectBadgeConfig = {
  label: 'Expensed',
  colorVariant: 'default',
  sortOrdinal: 3,
};

const NOT_CAPITALIZED_BADGE: ProjectBadgeConfig = {
  label: 'Not Capitalized',
  colorVariant: 'default',
  sortOrdinal: 4,
};

// Color encodes how much of the selected period is capitalized; the label (built below) carries the
// actual cap window so the coverage is legible without a tooltip (ASC-103). Label is spread on at the
// call site, so these carry only the color + sort order.
type CapCoverageStyle = Pick<ProjectBadgeConfig, 'colorVariant' | 'sortOrdinal'>;
const FULL_COVERAGE: CapCoverageStyle = { colorVariant: 'green', sortOrdinal: 1 };
const PARTIAL_COVERAGE: CapCoverageStyle = { colorVariant: 'yellow', sortOrdinal: 2 };

const buildOngoingLabel = (capitalizationStartDate: Date): string =>
  `Capitalized ${getCapitalizationDateString(capitalizationStartDate)} - Present`;

const buildBoundedLabel = (capitalizationStartDate: Date, capitalizationEndDate: Date): string =>
  `Capitalized ${getCapitalizationDateString(capitalizationStartDate)} - ${getCapitalizationDateString(
    capitalizationEndDate,
  )}`;

// Partial-coverage shorthand for a window that covers the period's first month but stops before its
// last: the start is redundant (it's at/before the period) so the label names only where cap ends.
const buildUntilLabel = (capitalizationEndDate: Date): string =>
  `Capitalized until ${getCapitalizationDateString(capitalizationEndDate)}`;

/**
 * Returns the badge config for a project given the currently selected period.
 *
 * The badge compares the project's capitalization window against the period at month-year
 * granularity so the modal stays consistent with the period-aware ASC wages table (ASC-103). Color
 * reflects period coverage (green = fully capitalized, yellow = partially capitalized) and the label
 * spells out the capitalization window for the green/yellow cases. A partially-covered window that
 * includes the period's first month but ends before its last is shortened to "Capitalized until
 * {end}", since its start is redundant. Projects with no capitalization in the period fall back to
 * the plain gray "Expensed" / "Not Capitalized" labels.
 *
 * `capitalizationStatus` is a STORED generated column on `asc_projects` derived from the date columns:
 *   NOT_CAPITALIZED ⇔ no start_date
 *   CAPITALIZED     ⇔ start_date set, end_date null (ongoing)
 *   EXPENSED        ⇔ both dates set
 * Any other combination indicates upstream data drift and is reported to Sentry.
 */
export const getAscCapitalizationBadgeConfig = ({
  capitalizationEndDate,
  capitalizationStartDate,
  capitalizationStatus,
  period,
}: {
  capitalizationStatus: CapitalizationStatus;
  capitalizationStartDate: Date | null;
  capitalizationEndDate: Date | null;
  period: MonthYearPeriod;
}): ProjectBadgeConfig => {
  const hasStart = capitalizationStartDate != null;
  const hasEnd = capitalizationEndDate != null;
  const invariantHolds =
    (capitalizationStatus === CapitalizationStatus.NotCapitalized && !hasStart && !hasEnd) ||
    (capitalizationStatus === CapitalizationStatus.Capitalized && hasStart && !hasEnd) ||
    (capitalizationStatus === CapitalizationStatus.Expensed && hasStart && hasEnd);

  if (!invariantHolds) {
    captureException(
      new Error(
        `Inconsistent ASC capitalization state: status=${capitalizationStatus} hasStart=${hasStart} hasEnd=${hasEnd}`,
      ),
    );
    return NOT_CAPITALIZED_BADGE;
  }

  if (capitalizationStatus === CapitalizationStatus.NotCapitalized) {
    return NOT_CAPITALIZED_BADGE;
  }

  if (capitalizationStatus === CapitalizationStatus.Capitalized) {
    const capStart = dateToMonthYear(capitalizationStartDate!);
    const label = buildOngoingLabel(capitalizationStartDate!);

    // Cap window starts entirely after the period — nothing capitalized in this period.
    if (isMonthYearBefore(period.endMonthYear, capStart)) {
      return NOT_CAPITALIZED_BADGE;
    }

    // Cap window started on or before the period — full coverage (still ongoing).
    if (!isMonthYearBefore(period.startMonthYear, capStart)) {
      return { ...FULL_COVERAGE, label };
    }

    // Cap window starts mid-period — partial coverage.
    return { ...PARTIAL_COVERAGE, label };
  }

  // CAPITALIZATION_STATUS.Expensed — bounded window, both dates present.
  const capStart = dateToMonthYear(capitalizationStartDate!);
  const capEnd = dateToMonthYear(capitalizationEndDate!);
  const label = buildBoundedLabel(capitalizationStartDate!, capitalizationEndDate!);

  // Cap window ended before the period started — fully amortized for this period.
  if (isMonthYearBefore(capEnd, period.startMonthYear)) {
    return EXPENSED_BADGE;
  }

  // Cap window starts entirely after the period — nothing capitalized in this period.
  if (isMonthYearBefore(period.endMonthYear, capStart)) {
    return NOT_CAPITALIZED_BADGE;
  }

  // Cap window fully covers the period.
  const startsOnOrBeforePeriod = !isMonthYearBefore(period.startMonthYear, capStart);
  const endsOnOrAfterPeriod = !isMonthYearBefore(capEnd, period.endMonthYear);
  if (startsOnOrBeforePeriod && endsOnOrAfterPeriod) {
    return { ...FULL_COVERAGE, label };
  }

  // Cap window covers the period's first month but stops before its last (we already returned for
  // full coverage above, so a true `startsOnOrBeforePeriod` here implies the window ends mid-period):
  // it includes the beginning of the period but not the end, so name only where cap stops (ASC-103).
  if (startsOnOrBeforePeriod) {
    return { ...PARTIAL_COVERAGE, label: buildUntilLabel(capitalizationEndDate!) };
  }

  return { ...PARTIAL_COVERAGE, label };
};
